import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { institutePortalRoles } from '@/lib/accessControl';
import type { UserRole } from '@/types';

interface ProtectedRouteProps {
    // Optional: when omitted, ProtectedRoute acts as a layout route and renders
    // <Outlet /> — so a whole group of child routes can share one role guard.
    children?: React.ReactNode;
    allowedRoles?: UserRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
    const { isAuthenticated, user, isLoading, logout, labSession } = useAuthStore();
    const location = useLocation();

    // Show loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
                    <p className="text-slate-500">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Students normally may only use the exam client — bounce them there. The one
    // exception is a BROWSER LAB LOGIN (learn.eswift.in/lab/login): that session is
    // explicitly allowed into the web student portal.
    if (user.role === 'student' && !labSession) {
        logout();
        return <Navigate to="/login?exam_client_only=1" state={{ from: location }} replace />;
    }

    // Check role permissions
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect to the role's OWN home — institute roles must never be sent to
        // /admin (the super-admin dashboard), or they'd bounce onto another
        // super-admin page. Super admins land on /admin.
        const redirectPath = institutePortalRoles.has(user.role)
            ? '/admin/institute-dashboard'
            : '/admin';
        return <Navigate to={redirectPath} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
}
